import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate, useLocation } from 'react-router-dom';
import { upipayment, confirmPayment } from "../../api";

function UPIPayment({ planId }) {
  const [upiData, setUpiData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  // For manual confirmation of UPI payment
  const [txnId, setTxnId] = useState('');
  const [confirmStatus, setConfirmStatus] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await upipayment(planId);
        setUpiData(data);
      } catch (err) {
        if (err.response && err.response.status === 401) {
          navigate('/login', { state: { from: location.pathname } });
        } else {
          setError("Something went wrong. Please try again later.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [planId, navigate, location.pathname]);

  // Redirect if user is not authenticated.
  if (!localStorage.getItem('authToken')) {
    return <Navigate to="/login" state={{ from: location.pathname }} />;
  }

  const styles = {
    container: {
      display: 'flex',
      gap: '20px',
      padding: '20px',
      fontFamily: 'Arial, sans-serif',
      flexWrap: 'wrap',
    },
    card: {
      flex: '1 1 300px',
      border: '1px solid #ccc',
      borderRadius: '12px',
      padding: '20px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      backgroundColor: '#fefefe',
    },
    heading: {
      fontSize: '20px',
      marginBottom: '15px',
      color: '#333',
    },
    qr: {
      margin: '20px 0',
      height: '200px',
      width: '200px',
    },
    link: {
      marginTop: '10px',
      display: 'inline-block',
      color: '#007bff',
      textDecoration: 'none',
      fontWeight: 'bold',
    },
    backButton: {
      margin: '10px 20px',
      fontSize: '16px',
      cursor: 'pointer',
      color: '#007bff',
      border: 'none',
      background: 'none',
      display: 'flex',
      alignItems: 'center',
    },
    arrow: {
      marginRight: '5px',
      fontSize: '18px',
    },
    error: {
      padding: '20px',
      color: 'red',
      textAlign: 'center',
    },
    confirmInput: {
      width: '100%',
      padding: '8px',
      marginBottom: '10px',
      boxSizing: 'border-box',
    },
    confirmButton: {
      padding: '10px 15px',
      backgroundColor: '#28a745',
      color: '#fff',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
    },
    statusMessage: {
      marginTop: '10px',
    },
  };

  if (loading) return <div style={{ padding: '20px' }}>Loading...</div>;
  if (error) return <div style={styles.error}>{error}</div>;
  if (!upiData) return null;

  const { upi_uri, upi_id, plan_name, amount, comment, plan_description, billing_cycle } = upiData;

  const handleConfirm = async () => {
    if (!txnId.trim()) {
      alert("Please enter a UPI Transaction ID.");
      return;
    }

    try {
      // Call your confirmPayment API with planId and txnId.
      const res = await confirmPayment({ plan_id: planId, txn_id: txnId,plan_price:amount,comment:comment});
      setConfirmStatus(res.message || "Payment confirmation submitted!");
    } catch (err) {
      setConfirmStatus("Error confirming payment. Please try again.");
    }
  };

  return (
    <>
      <button style={styles.backButton} onClick={() => navigate(-1)}>
        <span style={styles.arrow}>←</span> Back to Plans
      </button>

      <div style={styles.container}>
        {/* Left Tile - Plan Info */}
        <div style={styles.card}>
          <h2 style={styles.heading}>Selected Plan</h2>
          <p><strong>Name:</strong> {plan_name}</p>
          <p><strong>Description:</strong> {plan_description}</p>
          <p><strong>Price:</strong> ₹{amount}</p>
          <p><strong>Billing Cycle:</strong> {billing_cycle}</p>
        </div>

        {/* Right Tile - Payment Info */}
        <div style={styles.card}>
          <h2 style={styles.heading}>Pay with UPI</h2>
          <p><strong>Amount:</strong> ₹{amount}</p>
          <p><strong>UPI ID:</strong> {upi_id}</p>
          <p><strong>Payment Note:</strong> {comment}</p>

          <img
            src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(upi_uri)}`}
            alt="Scan to Pay"
            style={styles.qr}
          />
          <p>Scan the QR using any UPI app (PhonePe, GPay, Paytm)</p>
          <p>Or click to open in UPI app:</p>
          <a href={upi_uri} style={styles.link}>Pay with UPI</a>

          <hr />

          <h3>Confirm Payment</h3>
          <input
            type="text"
            placeholder="Enter UPI Transaction ID"
            value={txnId}
            onChange={(e) => setTxnId(e.target.value)}
            style={styles.confirmInput}
          />
          <button onClick={handleConfirm} style={styles.confirmButton}>
            Confirm Payment
          </button>
          {confirmStatus && <p style={styles.statusMessage}>{confirmStatus}</p>}
        </div>
      </div>
    </>
  );
}

export default UPIPayment;
